<template>
  <div v-show="displayEGiftRef" :class="$style.e_gift_form">
    <h3 :class="$style.modal_title">eギフトの設定</h3>
    <div v-if="(skuIsOutOfStock || !isConfirm) && !isHideMessage" :class="$style.warning_message">
      <p v-if="skuIsOutOfStock"> ※ 受け取る方は在庫がある色・サイズからのみ選択可能です</p>
      <p v-if="!isConfirm && !isHideMessage">
        ※ ラッピングが設定されていません。ラッピング希望の方は
        <AtomsConsumerButton :class="$style.wrapping_here" @click="emit('openGiftModal', true, false)">
          こちら
        </AtomsConsumerButton>
        より設定を行ってください
      </p>
    </div>
    <MoleculesConsumerFormItem>
      <template #title>受け取り主へのメッセージ</template>
      <AtomsConsumerTextarea
        v-model="localForm.messageValue"
        name="messageValue"
        placeholder="例）いつもありがとう"
        @update:model-value="onMessageChange"
      />
      <template #counter>
        <div> {{ localForm.messageValue.length }} / 200文字 </div>
      </template>
    </MoleculesConsumerFormItem>

    <MoleculesConsumerFormItem>
      <template #title>贈り主名</template>
      <AtomsConsumerToggle v-model="localForm.isAnonymous" name="isAnonymous" @update:model-value="onAnonymousChange">
        {{ isAnonymous ? "匿名でギフトを贈る" : "贈り主名を記載する" }}
      </AtomsConsumerToggle>
      <AtomsConsumerInput
        v-model="localForm.nameValue"
        :disabled="isAnonymous"
        name="nameValue"
        placeholder="例）鈴木花子"
        @update:model-value="onNameChange"
      />
      <template #footer-description>
        ギフトの受け取り画面・配送時の送り状に「{{
          isAnonymous ? "匿名の贈り主" : localForm.nameValue
        }}様からのお届け物です。」と記載されます
      </template>
      <template v-if="!isAnonymous" #counter>
        <div> {{ localForm.nameValue.length }} / 30文字 </div>
      </template>
    </MoleculesConsumerFormItem>

    <div :class="$style.button_wrap">
      <AtomsConsumerButton variant="primary" block @click="onConfirmEGift"> 入力内容を確定する </AtomsConsumerButton>
      <AtomsConsumerButton variant="secondary" block @click="emit('eGiftLift')">
        eギフトの設定を解除する
      </AtomsConsumerButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useToast } from "~/state"

const props = withDefaults(
  defineProps<{
    skuIsOutOfStock: boolean
    isHideMessage: boolean
    isConfirm: boolean
    displayEGiftRef: boolean
    messageValue: string
    nameValue: string
    isAnonymous: boolean
  }>(),
  {
    skuIsOutOfStock: false,
    isHideMessage: false,
    isConfirm: false,
    displayEGiftRef: false,
    isAnonymous: false,
  },
)

type Emits = {
  (e: "eGiftConfirm"): void
  (e: "eGiftLift"): void
  (e: "openGiftModal", isFromEGift: boolean, isFromEmbroidery: boolean): void
  (e: "update:nameValue", value: string): void
  (e: "update:messageValue", value: string): void
  (e: "update:isAnonymous", value: boolean): void
}

const localForm = ref({
  nameValue: props.nameValue,
  messageValue: props.messageValue,
  isAnonymous: props.isAnonymous,
})

const toast = useToast()

const emit = defineEmits<Emits>()

const checkStrLength = (text: string, type: "message" | "name"): { text: string; isError: boolean } => {
  let isError = false
  if (type === "message" && text.length > 200) {
    toast?.showErrorToasted("メッセージは200文字以内で入力してください")
    isError = true
  } else if (type === "name" && text.length > 30) {
    toast?.showErrorToasted("贈り主名は30文字以内で入力してください")
    isError = true
  }
  return { text, isError }
}

const onNameChange = (value: string) => {
  // eslint-disable-next-line no-irregular-whitespace
  const { text } = checkStrLength(value.replace(/^[ 　]+|[ 　]+$/g, ""), "name")
  localForm.value.nameValue = text
  emit("update:nameValue", text)
}

const onMessageChange = (value: string) => {
  const { text } = checkStrLength(value, "message")
  localForm.value.messageValue = text
  emit("update:messageValue", text)
}

const onAnonymousChange = (value: boolean) => {
  const isAnonymous: boolean = value
  localForm.value.nameValue = isAnonymous ? "匿名の贈り主" : localForm.value.nameValue
  emit("update:nameValue", localForm.value.nameValue)
  emit("update:isAnonymous", isAnonymous)
}

const onConfirmEGift = () => {
  if (checkStrLength(localForm.value.nameValue, "name").isError) return
  if (checkStrLength(localForm.value.messageValue, "message").isError) return
  emit("eGiftConfirm")
}
</script>

<style module scoped lang="scss">
.e_gift_form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: $max-width-sm;
  width: calc(100vw - 2rem);
  .modal_title {
    color: $primary;
  }
  .warning_message {
    color: $danger;
    text-align: left;
    padding: 0.5rem;
    font-size: $font-size-14;
    line-height: 1.8;
    .wrapping_here {
      display: inline-block;
      color: $primary;
      text-decoration: underline;
    }
  }
  .button_wrap {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 2rem;
  }
}
</style>
